import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { PublicEstateQrService } from 'src/app/shared/services/public-estate-qr.service';
import { DefectHelper } from 'src/app/view/defects/helpers/defect.helper';
import { EstateQrRouteData, parseEstateQrRouteData } from 'src/app/view/estate-view/helpers/estate-qr.helper';
import { RWDService } from '../../services/rwd.service';
import { buildPublicDefectStatusUrl } from '../../helpers/public-defect-status.helper';
import { EstateQrMessage, EstateQrPhoto } from './estate-qr-page.models';
import {
  collectEstateQrSpeechRecognitionTranscripts,
  EstateQrSpeechRecognition,
  EstateQrSpeechRecognitionErrorEvent,
  getEstateQrSpeechRecognitionConstructor,
  getEstateQrSpeechRecognitionErrorKey
} from './estate-qr-page.speech';
import {
  appendEstateQrPhotos,
  buildEstateQrPropertyDetailsSummary,
  EstateQrReportFormValue,
  buildEstateQrSubmitPayload,
  createEstateQrReportForm,
  createEstateQrMessage,
  getEstateQrSubmitErrorMessage,
  localizeEstateQrMessage,
  mergeEstateQrDescriptionValue
} from './estate-qr-page.utils';

@Component({
  selector: 'app-estate-qr-page',
  templateUrl: './estate-qr-page.component.html',
  styleUrls: ['./estate-qr-page.component.scss']
})
export class EstateQrPageComponent implements OnInit, OnDestroy {
  @ViewChild('photoInput') private photoInputRef?: ElementRef<HTMLInputElement>;

  public readonly maxPhotoCount = 5;
  public readonly maxSinglePhotoBytes = 1_500_000;
  public readonly maxTotalPhotoBytes = 6_000_000;
  public readonly acceptedPhotoExtensions = '.jpg,.jpeg,.png';

  public icon = 'build';
  public qrData: EstateQrRouteData | null = null;
  public reportForm: FormGroup;
  public photos: EstateQrPhoto[] = [];
  public isSubmitting = false;
  public isSubmitted = false;
  public submittedDefectId: string | null = null;
  public submittedRequestId: string | null = null;
  public confirmationEmailQueued = false;
  public publicStatusCode: string | null = null;
  public isSpeechRecognitionSupported = false;
  public isListeningDescription = false;
  public isQrDataLoading = true;

  private subscription$ = new Subscription();
  private speechRecognition: EstateQrSpeechRecognition | null = null;
  private photoErrorMessage: EstateQrMessage | null = null;
  private submitErrorMessage: EstateQrMessage | null = null;
  private speechRecognitionErrorMessage: EstateQrMessage | null = null;
  private qrDataResolveVersion = 0;

  constructor(
    public rwdService: RWDService,
    private route: ActivatedRoute,
    private ls: LocalizationService,
    private fb: FormBuilder,
    private publicEstateQrService: PublicEstateQrService,
    private zone: NgZone
  ) {
    this.reportForm = createEstateQrReportForm(this.fb);
  }

  ngOnInit(): void {
    if (this.route.snapshot.url[0].path === 'qr') this.icon = 'qr_code_2';
    this.ls.applyPreferredPublicLang();
    this.initSpeechRecognition();
    this.subscription$.add(
      this.route.queryParams.subscribe((params) => {
        void this.syncQrData(parseEstateQrRouteData(params));
      })
    );
  }

  ngOnDestroy(): void {
    this.destroySpeechRecognition();
    this.subscription$.unsubscribe();
  }

  public openPhotoPicker(): void {
    this.photoInputRef?.nativeElement.click();
  }

  public get photoError(): string | null {
    return localizeEstateQrMessage(this.photoErrorMessage, this.ls);
  }

  public get submitError(): string | null {
    return localizeEstateQrMessage(this.submitErrorMessage, this.ls);
  }

  public get speechRecognitionError(): string | null {
    return localizeEstateQrMessage(this.speechRecognitionErrorMessage, this.ls);
  }

  public get speechStatusKey(): string {
    return this.isListeningDescription ? 'estate.qrPublicSpeechActive' : 'estate.qrPublicSpeechIdle';
  }

  public async onPhotosSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const selectedFiles = Array.from(input?.files ?? []);
    if (!selectedFiles.length) return;

    const { nextPhotos, errorMessage } = await appendEstateQrPhotos(
      this.photos,
      selectedFiles,
      this.maxPhotoCount,
      this.maxSinglePhotoBytes,
      this.maxTotalPhotoBytes
    );

    this.photos = nextPhotos;
    this.photoErrorMessage = errorMessage;
    if (input) input.value = '';
  }

  public removePhoto(index: number): void {
    this.photos = this.photos.filter((_, photoIndex) => photoIndex !== index);
    this.photoErrorMessage = null;
  }

  public toggleDescriptionSpeechRecognition(): void {
    this.speechRecognitionErrorMessage = null;

    if (!this.speechRecognition) return;

    if (this.isListeningDescription) {
      this.stopSpeechRecognition();
      return;
    }

    this.speechRecognition.lang = this.ls.currentLocale;

    try {
      this.speechRecognition.start();
      this.isListeningDescription = true;
    } catch (error) {
      console.warn('Failed to start estate QR speech recognition', error);
      this.isListeningDescription = false;
      this.speechRecognitionErrorMessage = createEstateQrMessage('estate.qrPublicSpeechStartError');
    }
  }

  public async submitReport(): Promise<void> {
    if (this.isSubmitting) return;

    this.reportForm.markAllAsTouched();
    this.submitErrorMessage = null;
    this.publicStatusCode = null;
    this.photoErrorMessage = null;

    if (this.reportForm.invalid) return;

    if (!this.qrData?.estateId) {
      this.submitErrorMessage = createEstateQrMessage('estate.qrPublicInvalidPayload');
      return;
    }

    this.isSubmitting = true;

    try {
      const payload = buildEstateQrSubmitPayload(
        this.reportForm.getRawValue() as EstateQrReportFormValue,
        this.qrData,
        this.photos
      );
      const result = await this.publicEstateQrService.submitEstateQrDefect(payload);

      if (!result?.success) {
        this.submitErrorMessage = getEstateQrSubmitErrorMessage(result?.publicStatusCode);
        return;
      }

      this.submittedDefectId = result.defectId ?? null;
      this.submittedRequestId = result.refId ?? null;
      this.confirmationEmailQueued = result.confirmationEmailQueued === true;
      this.publicStatusCode = result.publicStatusCode ?? null;
      this.isSubmitted = true;
    } catch (error) {
      console.error('Failed to submit estate QR defect', error);
      this.submitErrorMessage = getEstateQrSubmitErrorMessage(null, (error as { code?: string | null } | null)?.code ?? null);
    } finally {
      this.isSubmitting = false;
    }
  }

  public isControlInvalid(controlName: string): boolean {
    const control = this.reportForm.get(controlName);
    return !!(control && control.touched && control.invalid);
  }

  public get qrLocationTypeLabel(): string | null {
    if (!this.qrData?.locationType) return null;
    return DefectHelper.getLocationTypeHR(this.qrData.locationType, this.qrData.estateType ?? EstateType.COMMUNITY, this.ls);
  }

  public get propertyDetailsSummary(): string {
    return buildEstateQrPropertyDetailsSummary(this.qrData, this.qrLocationTypeLabel, this.ls);
  }

  public get submittedPublicStatusUrl(): string | null {
    if (typeof window === 'undefined' || !this.submittedDefectId || !this.publicStatusCode) return null;
    return buildPublicDefectStatusUrl(window.location.origin, this.submittedDefectId, this.publicStatusCode);
  }

  private initSpeechRecognition(): void {
    if (typeof window === 'undefined') return;

    const SpeechRecognition = getEstateQrSpeechRecognitionConstructor(window);
    if (!SpeechRecognition) return;

    this.isSpeechRecognitionSupported = true;
    this.speechRecognition = new SpeechRecognition();
    this.speechRecognition.continuous = true;
    this.speechRecognition.interimResults = false;
    this.speechRecognition.lang = this.ls.currentLocale;
    this.speechRecognition.onresult = (event) => this.zone.run(() => this.handleSpeechRecognitionResult(event));
    this.speechRecognition.onerror = (event) => this.zone.run(() => this.handleSpeechRecognitionError(event));
    this.speechRecognition.onend = () =>
      this.zone.run(() => {
        this.isListeningDescription = false;
      });
  }

  private handleSpeechRecognitionResult(event: Parameters<NonNullable<EstateQrSpeechRecognition['onresult']>>[0]): void {
    const transcripts = collectEstateQrSpeechRecognitionTranscripts(event);
    if (!transcripts.length) return;

    const control = this.reportForm.get('description');
    if (!control) return;

    control.setValue(mergeEstateQrDescriptionValue(control.value, transcripts.join(' ')));
    control.markAsDirty();
    control.markAsTouched();
  }

  private handleSpeechRecognitionError(event: EstateQrSpeechRecognitionErrorEvent): void {
    this.isListeningDescription = false;
    this.speechRecognitionErrorMessage = createEstateQrMessage(getEstateQrSpeechRecognitionErrorKey(event.error));
  }

  private stopSpeechRecognition(): void {
    this.isListeningDescription = false;
    this.speechRecognition?.stop();
  }

  private destroySpeechRecognition(): void {
    if (!this.speechRecognition) return;

    this.isListeningDescription = false;
    this.speechRecognition.onresult = null;
    this.speechRecognition.onerror = null;
    this.speechRecognition.onend = null;
    this.speechRecognition.stop();
    this.speechRecognition = null;
  }

  private async syncQrData(routeQrData: EstateQrRouteData | null): Promise<void> {
    const resolveVersion = ++this.qrDataResolveVersion;
    this.isQrDataLoading = true;

    let qrData: EstateQrRouteData | null = null;

    try {
      qrData = await this.resolveQrData(routeQrData);
    } catch (error) {
      console.error('Failed to resolve public estate QR route data', error);
    }

    if (resolveVersion !== this.qrDataResolveVersion) return;

    this.qrData = qrData;
    this.isQrDataLoading = false;
  }

  private async resolveQrData(routeQrData: EstateQrRouteData | null): Promise<EstateQrRouteData | null> {
    if (!routeQrData) return null;
    if (!routeQrData.qrCodeId) return routeQrData.estateId ? routeQrData : null;
    return this.publicEstateQrService.getRouteData(routeQrData.qrCodeId);
  }
}
